import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Volume2, ScrollText, Sparkles, BookOpen, Type, Sun, Moon, ArrowRight, MessageSquare, HelpCircle, GitCompare, PlayCircle } from "lucide-react";
import { Tx } from "../context/AutoTranslateContext";
import {
  LETTERS, HARAKAT, WORDS, PRONOUNS,
  SUN_LETTERS, MOON_LETTERS, VERBS, SENTENCES, PREPOSITIONS, PARTICLES,
  SIMILAR_PAIRS, READING_DRILLS,
} from "../data/arabic";

const speak = (text, lang = "ar-SA") => {
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang; u.rate = 0.85;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch (e) { console.debug("speechSynthesis unavailable:", e); }
};

const TabBtn = ({ id, active, onClick, icon: Icon, children }) => (
  <button
    data-testid={`tab-${id}`}
    onClick={() => onClick(id)}
    className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition inline-flex items-center gap-1.5 ${
      active === id ? "bg-forest text-sand" : "bg-cream text-mute hover:text-forest"
    }`}
  >
    <Icon className="w-4 h-4" /> {children}
  </button>
);

const SectionTitle = ({ children }) => (
  <h2 className="font-heading text-xl text-forest font-semibold mb-4">{children}</h2>
);

const LetterCard = ({ letter, name, tr, iso, init, med, fin, examples }) => (
  <details data-testid={`letter-${name}`} className="bg-white rounded-2xl border border-gold/25 p-4 shadow-sm group">
    <summary className="cursor-pointer list-none">
      <div className="flex items-center justify-between mb-2">
        <span className="arabic text-5xl text-forest leading-none">{letter}</span>
        <div className="text-right">
          <p className="text-xs font-semibold text-ink">{name}</p>
          <p className="text-[10px] text-mute italic">{tr}</p>
        </div>
      </div>
      <p className="text-[10px] text-gold uppercase tracking-widest text-center group-open:hidden">
        <Tx>Tap for examples</Tx>
      </p>
    </summary>
    <div className="mt-3 space-y-3 border-t border-forest/10 pt-3">
      {/* All 4 positional forms */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-gold mb-1"><Tx>Four forms</Tx></p>
        <div className="grid grid-cols-4 gap-1 text-center">
          {[["Isolated",iso],["Initial",init],["Medial",med],["Final",fin]].map(([label,f]) => (
            <div key={label} className="bg-cream rounded-xl py-2">
              <p className="arabic text-2xl text-forest leading-none">{f}</p>
              <p className="text-[9px] text-mute mt-1"><Tx>{label}</Tx></p>
            </div>
          ))}
        </div>
      </div>
      {/* Example words */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-gold mb-1"><Tx>Example words</Tx></p>
        <div className="space-y-1">
          {examples.map(([ar,tr2,en], i) => (
            <button
              key={i}
              data-testid={`letter-${name}-ex-${i}`}
              onClick={(e) => { e.preventDefault(); speak(ar); }}
              className="w-full flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-cream text-left transition"
            >
              <span className="arabic text-lg text-forest flex-shrink-0 w-24 text-right">{ar}</span>
              <span className="text-xs italic text-ink flex-1">{tr2}</span>
              <span className="text-[10px] text-mute">{en}</span>
              <Volume2 className="w-3 h-3 text-mute" />
            </button>
          ))}
        </div>
      </div>
    </div>
  </details>
);

const ExampleRow = ({ ar, tr, en, testid }) => (
  <button
    data-testid={testid}
    onClick={() => speak(ar)}
    className="w-full flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-cream/60 text-left transition"
  >
    <span className="arabic text-lg text-forest flex-shrink-0 w-32 text-right">{ar}</span>
    <span className="text-xs italic text-ink flex-1 min-w-0">{tr}</span>
    <span className="text-xs text-mute">{en}</span>
    <Volume2 className="w-3 h-3 text-mute" />
  </button>
);

const ArabicLearn = () => {
  const [tab, setTab] = useState("letters");
  const [verbIdx, setVerbIdx] = useState(0);
  const currentVerb = VERBS[verbIdx];

  return (
    <div data-testid="arabic-learn-page" className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center gap-2 text-xs text-mute mb-6">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link>
        <span>/</span>
        <span className="text-forest"><Tx>Learn Arabic</Tx></span>
      </div>

      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3"><Tx>Academic foundations</Tx></p>
        <h1 className="font-heading text-4xl sm:text-5xl text-forest font-bold mb-3">
          <Tx>Learn Arabic from zero</Tx>
        </h1>
        <p className="text-mute leading-relaxed max-w-2xl mx-auto text-sm">
          <Tx>Alphabet with all four positional forms, top Qur'an vocabulary, six verb conjugations, pronouns, sun and moon letters, prepositions and particles — with many examples for every point. Tap any Arabic text to hear it aloud.</Tx>
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mb-8">
        <TabBtn id="letters"      active={tab} onClick={setTab} icon={ScrollText}><Tx>28 Letters</Tx></TabBtn>
        <TabBtn id="harakat"      active={tab} onClick={setTab} icon={Type}><Tx>Vowels & Harakāt</Tx></TabBtn>
        <TabBtn id="words"        active={tab} onClick={setTab} icon={Sparkles}><Tx>Top 30 words</Tx></TabBtn>
        <TabBtn id="pronouns"     active={tab} onClick={setTab} icon={BookOpen}><Tx>Pronouns</Tx></TabBtn>
        <TabBtn id="sunmoon"      active={tab} onClick={setTab} icon={Sun}><Tx>Sun & Moon letters</Tx></TabBtn>
        <TabBtn id="verbs"        active={tab} onClick={setTab} icon={Moon}><Tx>Verb conjugation</Tx></TabBtn>
        <TabBtn id="sentences"    active={tab} onClick={setTab} icon={BookOpen}><Tx>Sentence structure</Tx></TabBtn>
        <TabBtn id="prepositions" active={tab} onClick={setTab} icon={ArrowRight}><Tx>Prepositions</Tx></TabBtn>
        <TabBtn id="particles"    active={tab} onClick={setTab} icon={HelpCircle}><Tx>Questions & particles</Tx></TabBtn>
        <TabBtn id="similar"      active={tab} onClick={setTab} icon={GitCompare}><Tx>Similar sounds</Tx></TabBtn>
        <TabBtn id="drills"       active={tab} onClick={setTab} icon={PlayCircle}><Tx>Reading drills</Tx></TabBtn>
      </div>

      {tab === "letters" && (
        <div>
          <p className="text-sm text-mute mb-4 max-w-2xl">
            <Tx>Each letter shows its four positional forms plus eight example Qur'anic words. Tap the letter to expand — then tap any word to hear it.</Tx>
          </p>
          <div data-testid="letters-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {LETTERS.map(([ar, name, tr, iso, init, med, fin, examples]) => (
              <LetterCard key={name} letter={ar} name={name} tr={tr}
                iso={iso} init={init} med={med} fin={fin} examples={examples} />
            ))}
          </div>
        </div>
      )}

      {tab === "harakat" && (
        <div>
          <SectionTitle><Tx>Short vowels, sukūn, tanwīn and shaddah</Tx></SectionTitle>
          <p className="text-sm text-mute mb-6 max-w-2xl">
            <Tx>Long vowels are written as letters (ا و ي); short vowels are marks. Master these eight marks and you can read any voweled text. Every mark below comes with multiple examples.</Tx>
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {HARAKAT.map((h) => (
              <div key={h.name} data-testid={`harakah-${h.name}`}
                className="bg-white rounded-2xl border border-gold/25 p-5 shadow-sm">
                <div className="flex items-start gap-3 mb-3">
                  <span className="arabic text-3xl text-forest flex-shrink-0">{h.examples[0][0]}</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-ink">{h.name}</p>
                    <p className="text-[10px] text-gold italic">{h.tr}</p>
                    <p className="text-[10px] text-mute mt-1"><Tx>{h.note}</Tx></p>
                  </div>
                </div>
                <div className="border-t border-forest/10 pt-2 space-y-1">
                  {h.examples.map(([ar, tr], i) => (
                    <button key={i} data-testid={`harakah-${h.name}-ex-${i}`}
                      onClick={() => speak(ar)}
                      className="w-full flex items-center gap-3 py-1 px-2 rounded-lg hover:bg-cream/60 text-left">
                      <span className="arabic text-lg text-forest w-14 text-right">{ar}</span>
                      <span className="text-xs italic text-ink flex-1">{tr}</span>
                      <Volume2 className="w-3 h-3 text-mute" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "words" && (
        <div data-testid="words-grid" className="grid sm:grid-cols-2 gap-3">
          {WORDS.map((w, i) => (
            <ExampleRow key={i} testid={`word-${i}`} {...w} />
          ))}
        </div>
      )}

      {tab === "pronouns" && (
        <div>
          <SectionTitle><Tx>Detached pronouns (ad-ḍamā'ir al-munfaṣila)</Tx></SectionTitle>
          <p className="text-sm text-mute mb-6 max-w-2xl">
            <Tx>Arabic pronouns encode gender, number, and person. Master all twelve to address any speaker or subject correctly.</Tx>
          </p>
          <div className="grid sm:grid-cols-2 gap-2 bg-white rounded-3xl border border-gold/25 p-4 shadow-sm">
            {PRONOUNS.map((p, i) => (
              <ExampleRow key={i} testid={`pronoun-${i}`} {...p} />
            ))}
          </div>
        </div>
      )}

      {tab === "sunmoon" && (
        <div>
          <SectionTitle><Tx>Sun & Moon letters — al-lām ash-shamsiyyah wa al-qamariyyah</Tx></SectionTitle>
          <p className="text-sm text-mute mb-6 max-w-2xl">
            <Tx>When the definite article ال precedes a "sun letter", the ل is silent and the following letter is doubled with shaddah. Before a "moon letter", the ل is pronounced normally.</Tx>
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="clay-surface rounded-3xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sun className="w-5 h-5 text-gold" />
                <h3 className="font-heading text-lg text-forest font-semibold"><Tx>Sun letters</Tx></h3>
              </div>
              <p className="text-xs text-mute mb-3"><Tx>Example: الشَّمْس → "ash-shams", not "al-shams".</Tx></p>
              <div className="grid grid-cols-7 gap-2">
                {SUN_LETTERS.map((l) => (
                  <button key={l} data-testid={`sun-${l}`} onClick={() => speak(`ال${l}`)}
                    className="arabic text-2xl text-gold bg-white border border-gold/40 rounded-xl py-2 hover:bg-gold/10 transition">{l}</button>
                ))}
              </div>
            </div>
            <div className="clay-surface rounded-3xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Moon className="w-5 h-5 text-forest" />
                <h3 className="font-heading text-lg text-forest font-semibold"><Tx>Moon letters</Tx></h3>
              </div>
              <p className="text-xs text-mute mb-3"><Tx>Example: القَمَر → "al-qamar" — ل is pronounced.</Tx></p>
              <div className="grid grid-cols-7 gap-2">
                {MOON_LETTERS.map((l) => (
                  <button key={l} data-testid={`moon-${l}`} onClick={() => speak(`ال${l}`)}
                    className="arabic text-2xl text-forest bg-white border border-forest/25 rounded-xl py-2 hover:bg-forest/5 transition">{l}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "verbs" && (
        <div>
          <SectionTitle><Tx>Verb conjugation — six common verbs</Tx></SectionTitle>
          <p className="text-sm text-mute mb-4 max-w-2xl">
            <Tx>Every Arabic verb comes from a three-letter root. Study six of the most common verbs across all eight persons in past (māḍī) and present (muḍāri') tenses.</Tx>
          </p>
          <div className="flex flex-wrap gap-2 mb-5">
            {VERBS.map((v, i) => (
              <button key={v.root} data-testid={`verb-tab-${i}`}
                onClick={() => setVerbIdx(i)}
                className={`px-4 py-2 rounded-full text-xs font-medium transition ${verbIdx === i ? "bg-gold text-forest" : "bg-cream text-mute hover:text-forest"}`}>
                <span className="arabic mr-2">{v.past3ms}</span>
                <span className="italic">{v.english}</span>
              </button>
            ))}
          </div>
          <div className="mb-3 text-center">
            <p className="text-[10px] uppercase tracking-widest text-gold">
              <Tx>Root</Tx>: <span className="arabic text-base text-forest">{currentVerb.root}</span> · <Tx>Meaning</Tx>: <span className="italic">{currentVerb.english}</span>
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-gold/25 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-cream text-mute text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3"><Tx>Subject</Tx></th>
                  <th className="text-center px-4 py-3"><Tx>Past (māḍī)</Tx></th>
                  <th className="text-center px-4 py-3"><Tx>Present (muḍāri')</Tx></th>
                </tr>
              </thead>
              <tbody>
                {currentVerb.rows.map((v, i) => (
                  <tr key={i} data-testid={`verb-row-${i}`} className="border-t border-forest/10 hover:bg-cream/50">
                    <td className="px-4 py-3 text-ink">{v.subject}</td>
                    <td className="px-4 py-3 arabic text-2xl text-forest text-center cursor-pointer hover:text-gold" onClick={() => speak(v.past)}>{v.past}</td>
                    <td className="px-4 py-3 arabic text-2xl text-forest text-center cursor-pointer hover:text-gold" onClick={() => speak(v.present)}>{v.present}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "sentences" && (
        <div>
          <SectionTitle><Tx>Sentence structure — nominal & verbal</Tx></SectionTitle>
          <p className="text-sm text-mute mb-6 max-w-2xl">
            <Tx>Arabic sentences come in two types: nominal (starting with a noun) and verbal (starting with a verb). There is no verb "to be" in the present — juxtaposition is enough. Below are 15 worked examples with iʿrāb notes.</Tx>
          </p>
          <div className="space-y-3">
            {SENTENCES.map((s, i) => (
              <button key={i} data-testid={`sentence-${i}`} onClick={() => speak(s.ar)}
                className="w-full bg-white rounded-2xl border border-gold/25 p-5 text-left hover:border-forest transition shadow-sm">
                <div className="arabic text-2xl text-forest mb-2 leading-relaxed" dir="rtl">{s.ar}</div>
                <p className="text-sm text-ink italic">{s.tr}</p>
                <p className="text-sm text-mute mb-2">{s.en}</p>
                <p className="text-[11px] text-gold border-t border-gold/20 pt-2"><Tx>{s.note}</Tx></p>
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === "prepositions" && (
        <div>
          <SectionTitle><Tx>Prepositions — ḥurūf al-jarr</Tx></SectionTitle>
          <p className="text-sm text-mute mb-6 max-w-2xl">
            <Tx>Every preposition puts the noun after it into the genitive case (kasrah ending). Learn the nine most common with three examples each.</Tx>
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {PREPOSITIONS.map((p, i) => (
              <div key={i} data-testid={`prep-${i}`} className="bg-white rounded-2xl border border-gold/25 p-4 shadow-sm">
                <div className="flex items-baseline gap-3 mb-3 border-b border-forest/10 pb-2">
                  <span className="arabic text-2xl text-forest">{p.ar}</span>
                  <span className="text-xs italic text-ink">{p.tr}</span>
                  <span className="text-[11px] text-mute ml-auto">{p.en}</span>
                </div>
                <div className="space-y-1">
                  {p.examples.map(([ar, tr, en], j) => (
                    <ExampleRow key={j} testid={`prep-${i}-ex-${j}`} ar={ar} tr={tr} en={en} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "particles" && (
        <div className="space-y-8">
          <div>
            <SectionTitle><Tx>Question words (adawāt al-istifhām)</Tx></SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              {PARTICLES.interrogatives.map((q, i) => (
                <button key={i} data-testid={`q-${i}`} onClick={() => speak(q.ex[0])}
                  className="bg-white rounded-2xl border border-gold/25 p-4 shadow-sm text-left hover:border-forest transition">
                  <div className="flex items-baseline gap-3 mb-2 border-b border-forest/10 pb-2">
                    <span className="arabic text-2xl text-forest">{q.ar}</span>
                    <span className="text-xs italic text-ink">{q.tr}</span>
                    <span className="text-[11px] text-mute ml-auto"><Tx>{q.en}</Tx></span>
                  </div>
                  <p className="arabic text-lg text-forest">{q.ex[0]}</p>
                  <p className="text-xs italic text-ink">{q.ex[1]}</p>
                  <p className="text-xs text-mute">{q.ex[2]}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <SectionTitle><Tx>Negation particles (adawāt an-nafy)</Tx></SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              {PARTICLES.negation.map((q, i) => (
                <button key={i} data-testid={`neg-${i}`} onClick={() => speak(q.ex[0])}
                  className="bg-white rounded-2xl border border-gold/25 p-4 shadow-sm text-left hover:border-forest transition">
                  <div className="flex items-baseline gap-3 mb-2 border-b border-forest/10 pb-2">
                    <span className="arabic text-2xl text-forest">{q.ar}</span>
                    <span className="text-xs italic text-ink">{q.tr}</span>
                    <span className="text-[11px] text-mute ml-auto"><Tx>{q.en}</Tx></span>
                  </div>
                  <p className="arabic text-lg text-forest">{q.ex[0]}</p>
                  <p className="text-xs italic text-ink">{q.ex[1]}</p>
                  <p className="text-xs text-mute">{q.ex[2]}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <SectionTitle><Tx>Conjunctions (ḥurūf al-ʿaṭf)</Tx></SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              {PARTICLES.conjunctions.map((q, i) => (
                <button key={i} data-testid={`conj-${i}`} onClick={() => speak(q.ex[0])}
                  className="bg-white rounded-2xl border border-gold/25 p-4 shadow-sm text-left hover:border-forest transition">
                  <div className="flex items-baseline gap-3 mb-2 border-b border-forest/10 pb-2">
                    <span className="arabic text-2xl text-forest">{q.ar}</span>
                    <span className="text-xs italic text-ink">{q.tr}</span>
                    <span className="text-[11px] text-mute ml-auto"><Tx>{q.en}</Tx></span>
                  </div>
                  <p className="arabic text-lg text-forest">{q.ex[0]}</p>
                  <p className="text-xs italic text-ink">{q.ex[1]}</p>
                  <p className="text-xs text-mute">{q.ex[2]}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "similar" && (
        <div>
          <SectionTitle><Tx>Similar-sounding letters — the classic contrastive drill</Tx></SectionTitle>
          <p className="text-sm text-mute mb-6 max-w-2xl">
            <Tx>Non-Arab speakers regularly confuse these pairs — the difference changes the meaning of the word entirely. Tap each Arabic word to hear it and train your ear.</Tx>
          </p>
          <div className="space-y-4">
            {SIMILAR_PAIRS.map((p, i) => (
              <div key={i} data-testid={`similar-${i}`} className="bg-white rounded-2xl border border-gold/25 p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-3 border-b border-forest/10 pb-3">
                  <div className="flex gap-2">
                    {p.pair.map((l, j) => (
                      <span key={j} className="arabic text-4xl text-forest bg-cream w-14 h-14 rounded-2xl flex items-center justify-center">{l}</span>
                    ))}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-ink">{p.names.join(" · ")}</p>
                    <p className="text-xs text-gold italic"><Tx>{p.hint}</Tx></p>
                  </div>
                </div>
                <div className="space-y-2">
                  {p.examples.map(([w1, w2, gloss], j) => (
                    <div key={j} data-testid={`similar-${i}-ex-${j}`} className="flex items-center gap-3 text-sm">
                      <button onClick={() => speak(w1)} className="arabic text-xl text-forest bg-cream px-3 py-1 rounded-lg hover:text-gold transition min-w-[80px] text-right">{w1}</button>
                      <span className="text-mute text-xs">vs</span>
                      <button onClick={() => speak(w2)} className="arabic text-xl text-forest bg-cream px-3 py-1 rounded-lg hover:text-gold transition min-w-[80px] text-right">{w2}</button>
                      <span className="text-xs text-mute flex-1">{gloss}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "drills" && (
        <div>
          <SectionTitle><Tx>Reading drills — connected phrases</Tx></SectionTitle>
          <p className="text-sm text-mute mb-6 max-w-2xl">
            <Tx>Once you know the letters and vowels, practise reading connected phrases aloud. Tap each phrase to hear the correct pronunciation.</Tx>
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {READING_DRILLS.map(([ar, tr, en], i) => (
              <button key={i} data-testid={`drill-${i}`} onClick={() => speak(ar)}
                className="bg-white rounded-2xl border border-gold/25 p-4 shadow-sm text-left hover:border-forest transition">
                <p className="arabic text-2xl text-forest mb-2 leading-relaxed" dir="rtl">{ar}</p>
                <p className="text-xs italic text-ink">{tr}</p>
                <p className="text-xs text-mute">{en}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12 text-center bg-cream/60 rounded-3xl p-6 border border-gold/20">
        <p className="text-xs text-mute italic">
          <Tx>Deeper lessons on your way — noun cases (raf', naṣb, jarr), Forms II-X verb patterns, broken plurals, and full iʿrāb of Qur'anic ayāt inshā'Allāh.</Tx>
        </p>
      </div>
    </div>
  );
};

export default ArabicLearn;
